import React from 'react'
import { useTheme } from '@mui/material/styles'
import { DatePicker, type DatePickerProps } from '@mui/x-date-pickers'

import { FormHelperText } from '~components/common'

// Custom interface for TextField slot props that includes error and helperText
interface CustomTextFieldSlotProps {
  error?: boolean
  helperText?: string
  id?: string
}

interface CustomDatePickerProps extends Omit<DatePickerProps, 'open' | 'onOpen' | 'onClose'> {
  clearable?: boolean
}

const CustomDatePicker = (props: CustomDatePickerProps): React.ReactElement => {
  const [open, setOpen] = React.useState<boolean>(false)

  const theme = useTheme()

  // Extract error states from both sources
  const textFieldSlotProps = props.slotProps?.textField as CustomTextFieldSlotProps | undefined
  const hasError = textFieldSlotProps?.error
  const helperText = textFieldSlotProps?.helperText

  const handleClose = (): void => {
    const activeElement = document.activeElement
    const isInputElement = activeElement?.tagName === 'INPUT'

    if (!isInputElement) {
      setOpen(false)
    }
  }

  const placeHolder = props.disabled ? { placeholder: '' } : {}

  const hasLabel =
    props.label !== undefined ||
    (props.slotProps?.textField as { label?: string })?.label !== undefined
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <DatePicker
        {...props}
        open={open}
        onClose={handleClose}
        slotProps={{
          ...props.slotProps,
          field: {
            clearable: props.clearable ?? false,
            ...props.slotProps?.field,
          },
          textField: {
            id: textFieldSlotProps?.id,
            ...props.slotProps?.textField,
            onClick: () => {
              if (!props.disabled) setOpen(!open)
            },
            ...placeHolder,
            slotProps: {
              formHelperText: {
                sx: {
                  display: 'none', // Hide the default helper text
                },
              },
            },
            sx: {
              backgroundColor: props.disabled ? theme.palette.action.disabledInput : 'white',
              '& .MuiInputBase-root': {
                cursor: 'pointer',
              },
              '& .MuiInputBase-input': {
                cursor: 'text',
              },
              '& .MuiInputBase-input::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 1,
              },
              '& .MuiOutlinedInput-notchedOutline': !hasLabel
                ? {
                    border: 'none',
                    boxShadow: props.disabled
                      ? `inset 0 0 0 1px ${theme.palette.input.disabled}`
                      : hasError
                        ? `inset 0 0 0 2px ${theme.palette.error.main}`
                        : `inset 0 0 0 1px ${theme.palette.input.default}`,
                    transition: theme.transitions.create(['box-shadow']),
                  }
                : {
                    border: `1px solid ${theme.palette.input.default}`,
                  },
              '& .MuiOutlinedInput-root:not(.Mui-disabled):hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline':
                !hasLabel
                  ? {
                      boxShadow: `inset 0 0 0 2px ${theme.palette.input.hover}`,
                    }
                  : { border: `2px solid ${theme.palette.input.hover}` },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': !hasLabel
                ? {
                    boxShadow: `inset 0 0 0 2px ${theme.palette.input.hover}, 0 0 0 2px ${theme.palette.input.focus}`,
                    outline: 'none',
                  }
                : { border: `2px solid ${theme.palette.input.hover}` },
              '& .MuiOutlinedInput-root.Mui-focused:hover .MuiOutlinedInput-notchedOutline':
                !hasLabel
                  ? {
                      boxShadow: `inset 0 0 0 2px ${theme.palette.input.hover}, 0 0 0 2px ${theme.palette.input.focus}`,
                      outline: 'none',
                    }
                  : { border: `2px solid ${theme.palette.input.hover}` },
            },
          },
          openPickerButton: {
            ...props.slotProps?.openPickerButton,
            onClick: () => {
              if (!props.disabled) setOpen(!open)
            },
          },
          popper: {
            ...props.slotProps?.popper,
            sx: {
              zIndex: 1300,
            },
          },
        }}
      />
      {helperText && (
        <FormHelperText
          id={textFieldSlotProps?.id}
          aria-describedby={textFieldSlotProps?.id}
          error={hasError}
          isVisible={hasError || !!helperText}
        >
          {helperText}
        </FormHelperText>
      )}
    </div>
  )
}

export default CustomDatePicker
